import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AutoCompleteCompleteEvent, AutoCompleteSelectEvent } from 'primeng/autocomplete';
import { Subscription } from 'rxjs';
import { NavigationService } from 'src/app/shared/navigation/navigation.service';
import { AccountingPackageService } from '../shared/accounting-package/accounting-package.service';
import { CreditCardGatewayService, initGatewayTypes } from '../shared/credit-card-gateway/credit-card-gateway.service';
import { PaymentTypeService } from '../shared/payment-types/payment-type.service';
import { RevenueItemService } from '../shared/revenue-items/revenue-item.service';

export type SelectorKind = 'item' | 'accountingPackage' | 'paymentType' | 'gateway';
export interface SelectorOption { id: number; label: string; }
export const SELECTOR_QUERY_PARAMS: Record<SelectorKind, string> = {
  item: 'itemNum', accountingPackage: 'accountingPackageId', paymentType: 'paymentTypeId', gateway: 'ccGatewayId',
};
export const SELECTOR_INVOICE_QUERY_PARAM = 'invoiceNum';
export const MAX_SUGGESTIONS = 50;

const GATEWAY_TYPES = initGatewayTypes();

// Allow-list raw shapes: only the fields read are declared; credentials are never touched.
interface LegacyItemRecord { ItemNum?: number; ItemName?: string; Descr?: string; }
interface LegacyAccountingPackageRecord { AccountingPackageId?: number; AccountingPackageID?: number; Name?: string; }
interface LegacyPaymentTypeRecord { PaymentTypeId?: number; PaymentTypeID?: number; PaymentType?: string; Descr?: string; }
interface LegacyGatewayRecord {
  CCGatewayID?: number; CcGatewayId?: number; Name?: string; GatewayTypeID?: number; GatewayTypeId?: number;
}

function mapItem(raw: LegacyItemRecord): SelectorOption | null {
  const id = raw?.ItemNum;
  if (typeof id !== 'number') { return null; }
  return { id, label: `${raw.ItemName || raw.Descr || 'Item'} (#${id})` };
}

function mapAccountingPackage(raw: LegacyAccountingPackageRecord): SelectorOption | null {
  const id = raw?.AccountingPackageId ?? raw?.AccountingPackageID;
  if (typeof id !== 'number') { return null; }
  return { id, label: `${raw.Name || 'Package'} (#${id})` };
}

function mapPaymentType(raw: LegacyPaymentTypeRecord): SelectorOption | null {
  const id = raw?.PaymentTypeId ?? raw?.PaymentTypeID;
  if (typeof id !== 'number') { return null; }
  return { id, label: `${raw.PaymentType || raw.Descr || 'Payment Type'} (#${id})` };
}

function mapGateway(raw: LegacyGatewayRecord): SelectorOption | null {
  const id = raw?.CCGatewayID ?? raw?.CcGatewayId;
  if (typeof id !== 'number') { return null; }
  const name = raw.Name || 'Gateway';
  const typeId = raw.GatewayTypeID ?? raw.GatewayTypeId;
  const typeName = GATEWAY_TYPES.find(t => t.GatewayTypeId === typeId)?.GatewayTypeName;
  return { id, label: typeName ? `${name} - ${typeName} (#${id})` : `${name} (#${id})` };
}

function perKind<T>(value: () => T): Record<SelectorKind, T> {
  return { item: value(), accountingPackage: value(), paymentType: value(), gateway: value() };
}

@Component({
  standalone: false,
  selector: 'wl-gateway-resolution-selector',
  templateUrl: './gateway-resolution-selector.component.html',
  styleUrls: ['./gateway-resolution-selector.component.less'],
})
export class GatewayResolutionSelectorComponent implements OnInit, OnDestroy {
  readonly kinds: SelectorKind[] = ['item', 'accountingPackage', 'paymentType', 'gateway'];

  options: Record<SelectorKind, SelectorOption[]> = perKind<SelectorOption[]>(() => []);
  suggestions: Record<SelectorKind, SelectorOption[]> = perKind<SelectorOption[]>(() => []);
  selected: Record<SelectorKind, SelectorOption | null> = perKind<SelectorOption | null>(() => null);
  loading: Record<SelectorKind, boolean> = perKind<boolean>(() => false);
  loadFailed: Record<SelectorKind, boolean> = perKind<boolean>(() => false);

  invoiceText = '';
  invoiceInvalid = false;

  private currentInvoice: number | null = null;
  private currentIds: Record<SelectorKind, number | null> = perKind<number | null>(() => null);
  private subscription?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private _navigationService: NavigationService,
    private _gatewayService: CreditCardGatewayService,
    private _itemService: RevenueItemService,
    private _paymentTypeService: PaymentTypeService,
    private _accountingPackageService: AccountingPackageService,
  ) {}

  ngOnInit(): void {
    this.subscription?.unsubscribe();
    this.subscription = this.route.queryParamMap.subscribe(params => {
      for (const kind of this.kinds) {
        this.currentIds[kind] = this.parseId(params.get(SELECTOR_QUERY_PARAMS[kind]));
      }
      this.currentInvoice = this.parseId(params.get(SELECTOR_INVOICE_QUERY_PARAM));
      if (this.currentInvoice === 0) { this.currentInvoice = null; }
      this.invoiceText = this.currentInvoice === null ? '' : String(this.currentInvoice);
      this.invoiceInvalid = false;
      this.resolveAllSelections();
    });

    this.loadList('item', () => this._itemService.getAllRevenueItems(), mapItem);
    this.loadList('accountingPackage', () => this._accountingPackageService.getAccountingPackages(), mapAccountingPackage);
    this.loadList('paymentType', () => this._paymentTypeService.getPaymentTypes(), mapPaymentType);
    this.loadList('gateway', () => this._gatewayService.getAllCreditCardGateways(), mapGateway);
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  search(kind: SelectorKind, event: AutoCompleteCompleteEvent): void {
    const q = (event.query ?? '').trim().toLowerCase();
    const matches = q === ''
      ? this.options[kind]
      : this.options[kind].filter(o => o.label.toLowerCase().includes(q) || String(o.id).includes(q));
    this.suggestions[kind] = matches.slice(0, MAX_SUGGESTIONS);
  }

  select(kind: SelectorKind, event: AutoCompleteSelectEvent): void {
    const queryParams: Record<string, number | null> = {};
    for (const k of this.kinds) {
      queryParams[SELECTOR_QUERY_PARAMS[k]] = k === kind ? (event.value as SelectorOption).id : null;
    }
    queryParams[SELECTOR_INVOICE_QUERY_PARAM] = null;
    void this._navigationService.setCurrentQueryParams(queryParams, this.route);
  }

  applyInvoice(): void {
    const text = (this.invoiceText ?? '').trim();
    if (text === '') { return; }
    if (!/^\d+$/.test(text) || Number(text) === 0) {
      this.invoiceInvalid = true;
      return;
    }
    const n = Number(text);
    if (n === this.currentInvoice) {
      this.invoiceInvalid = false;
      return;
    }
    this.invoiceInvalid = false;
    this.currentInvoice = n;
    void this._navigationService.setCurrentQueryParams(
      { itemNum: null, accountingPackageId: null, paymentTypeId: null, ccGatewayId: null, [SELECTOR_INVOICE_QUERY_PARAM]: n },
      this.route,
    );
  }

  clearInvoice(): void {
    this.invoiceText = '';
    this.invoiceInvalid = false;
    if (this.currentInvoice === null) { return; }
    this.currentInvoice = null;
    void this._navigationService.setCurrentQueryParams({ [SELECTOR_INVOICE_QUERY_PARAM]: null }, this.route);
  }

  clear(kind: SelectorKind): void {
    if (this.currentIds[kind] == null) { return; }
    void this._navigationService.setCurrentQueryParams({ [SELECTOR_QUERY_PARAMS[kind]]: null }, this.route);
  }

  private loadList(kind: SelectorKind, fetch: () => Promise<unknown>, map: (raw: any) => SelectorOption | null): void {
    this.loading[kind] = true;
    let promise: Promise<unknown>;
    try {
      promise = fetch();
    } catch {
      this.markFailed(kind);
      return;
    }
    Promise.resolve(promise)
      .then(result => {
        if (!Array.isArray(result)) {
          this.markFailed(kind);
          return;
        }
        this.options[kind] = result
          .map(map)
          .filter((o): o is SelectorOption => o !== null)
          .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
        this.resolveSelection(kind);
      })
      .catch(() => this.markFailed(kind))
      .finally(() => { this.loading[kind] = false; });
  }

  private markFailed(kind: SelectorKind): void {
    this.options[kind] = [];
    this.loadFailed[kind] = true;
    this.loading[kind] = false;
    this.resolveSelection(kind);
  }

  private resolveSelection(kind: SelectorKind): void {
    const id = this.currentIds[kind];
    this.selected[kind] = id == null
      ? null
      : this.options[kind].find(o => o.id === id) ?? { id, label: String(id) };
  }

  private resolveAllSelections(): void {
    for (const kind of this.kinds) { this.resolveSelection(kind); }
  }

  private parseId(value: string | null): number | null {
    return value !== null && /^\d+$/.test(value) ? Number(value) : null;
  }
}
